/**
 * tm/components/ClientAsksPanel.tsx — 거래처 기한 「자료 요청」 탭(2026-10-25 지도사 「내가 어떤 설정을 하면 팀장님들이 각 화면에서
 *   고객에게 받아 올리고, 내가 검토 보고서를 보내면 각 화면에서 보이게」 · 첫 쓰임 가결산)
 *  - 새 자료 요청(지도사만): 이름 · 받을 것(한 줄에 하나) · 팀장님께 보일 안내 · 기한 · 대상(법인 전체 · 모든 거래처 · 골라서) · 보고서 이름
 *  - 요청 목록: 상태별 수(올려 주세요 · 올림 · 검토 중 · 보고서 보냄)
 *  - 요청 하나: 업체 줄마다 올린 파일 열기 · 검토 중으로 · 더 올려 주세요(무엇이 필요한지) · 보고서 보내기(PDF) · 빼기
 *  서버 = routes/tm-clientdesk.js(/client-desk/asks…) · services/docAsk.js
 */
import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { req, apiBase, apiToken } from '../api';
import { C, T } from './ui';

interface AskRow { id: string; title: string; items: string[]; guide: string; due_date: string; target: string; report_title: string; status: string; created_at: string; counts: Record<string, number> }
interface FileV { id: string; name: string; day: string | null }
interface Line { id: string; company_id: string; company_name: string; ceo: string; fps: string[]; status: string; status_ko: string; note: string; uploaded_at: string | null; reported_at: string | null; files: FileV[]; report: FileV | null }
interface Detail { ask: AskRow; rows: Line[] }
interface Target { id: string; name: string; biz_no: string }

const NAVY = '#1B3A5C';
const MUTED = '#6B7280';
const ell: CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 };
const card: CSSProperties = { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' };
const input: CSSProperties = { height: 36, padding: '0 10px', borderRadius: 8, border: `1px solid ${C.borderDk}`, fontSize: T.md, fontFamily: 'inherit', boxSizing: 'border-box', width: '100%' };
const btn = (kind: 'pri' | 'line' | 'quiet' = 'line', off = false): CSSProperties => ({
  height: 32, padding: '0 12px', borderRadius: 8, fontSize: T.md, fontWeight: 700, cursor: off ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit',
  border: off ? '1px solid #E5E7EB' : kind === 'pri' ? `1px solid ${NAVY}` : kind === 'line' ? `1px solid ${C.borderDk}` : 'none',
  background: off ? '#F3F4F6' : kind === 'pri' ? NAVY : kind === 'line' ? '#fff' : 'transparent', color: off ? '#9CA3AF' : kind === 'pri' ? '#fff' : kind === 'line' ? C.text : C.sub,
});
const WD = '일월화수목금토';
const md = (d: string | null) => (d && d.length >= 10 ? `${+d.slice(5, 7)}/${+d.slice(8, 10)}(${WD.charAt(new Date(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10)).getDay())})` : '');
const TONE: Record<string, [string, string]> = {
  asked: ['#F3F4F6', '#374151'], more: ['#FEF3C7', '#92400E'], uploaded: ['#E0ECFA', '#1B3A5C'], reviewing: ['#EDE9FE', '#5B21B6'], reported: ['#DCFCE7', '#166534'], skipped: ['#F3F4F6', MUTED],
};
const KO: Record<string, string> = { asked: '올려 주세요', more: '더 올려 주세요', uploaded: '올림', reviewing: '검토 중', reported: '보고서 보냄', skipped: '빼기' };
const TARGET_KO: Record<string, string> = { corp: '법인 거래처', all: '모든 거래처', pick: '고른 거래처' };
/** 가결산 예시 — 「예시로 채우기」(지도사가 고쳐서 쓴다) */
const PRECLOSE = { title: '가결산 자료', items: '시산표(1~9월)\n직전 연도 재무제표\n가지급금 · 가수금 명세(있으면)', guide: '연말 결산 전에 절세 방안을 미리 검토해 드려요. 대표님께 받아 올려 주세요.' };

function Pill({ s, n }: { s: string; n?: number }) {
  const [bg, fg] = TONE[s] || TONE.asked;
  return <span style={{ background: bg, color: fg, borderRadius: 999, fontSize: T.sm, fontWeight: 800, padding: '3px 10px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{KO[s] || s}{n != null ? ` ${n}` : ''}</span>;
}

export default function ClientAsksPanel({ canEdit, narrow, onOpenCompany, onToast }: { canEdit: boolean; narrow: boolean; onOpenCompany: (id: string) => void; onToast: (m: string, k?: 'success' | 'error') => void }) {
  const [asks, setAsks] = useState<AskRow[] | null>(null);
  const [err, setErr] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [making, setMaking] = useState(false);
  const [busy, setBusy] = useState('');
  const [more, setMore] = useState<{ id: string; text: string } | null>(null);

  const load = useCallback(async () => {
    try { const r = await req<AskRow[]>('GET', '/client-desk/asks'); if (r.success) { setAsks(r.data); setErr(''); } else setErr(r.error || '불러오지 못했어요'); }
    catch (e) { setErr(e instanceof Error ? e.message : '불러오지 못했어요'); }
  }, []);
  const loadDetail = useCallback(async (id: string) => {
    try { const r = await req<Detail>('GET', `/client-desk/asks/${id}`); if (r.success) setDetail(r.data); else onToast(r.error || '불러오지 못했어요', 'error'); }
    catch (e) { onToast(e instanceof Error ? e.message : '불러오지 못했어요', 'error'); }
  }, [onToast]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (openId) loadDetail(openId); else setDetail(null); }, [openId, loadDetail]);

  async function setRow(l: Line, status: string, note = '', done = '') {
    if (busy) return;
    setBusy(l.id);
    try {
      const r = await req('PATCH', `/client-desk/ask-rows/${l.id}`, { status, note });
      if (r.success) { onToast(done || `${l.company_name} · ${KO[status]}`, 'success'); setMore(null); if (openId) await loadDetail(openId); await load(); }
      else onToast(r.error || '바꾸지 못했어요', 'error');
    } finally { setBusy(''); }
  }
  async function sendReport(l: Line, f: File) {
    if (busy) return;
    setBusy(l.id);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await fetch(`${apiBase()}/client-desk/ask-rows/${l.id}/report`, { method: 'POST', headers: { Authorization: 'Bearer ' + apiToken() }, body: fd });
      const j = await res.json().catch(() => ({})) as { success?: boolean; error?: string };
      if (j.success) { onToast(`${l.company_name} · 보고서를 보냈어요. 팀장님 화면에 보여요.`, 'success'); if (openId) await loadDetail(openId); await load(); }
      else onToast(j.error || '보내지 못했어요', 'error');
    } catch (e) { onToast(e instanceof Error ? e.message : '보내지 못했어요', 'error'); }
    finally { setBusy(''); }
  }
  async function openFile(id: string) {
    try { const r = await req<{ url: string }>('GET', `/client-desk/ask-files/${id}`); if (r.success && r.data.url) window.open(r.data.url, '_blank', 'noopener'); else onToast(r.error || '열지 못했어요', 'error'); }
    catch (e) { onToast(e instanceof Error ? e.message : '열지 못했어요', 'error'); }
  }
  async function close(open: boolean) {
    if (!detail) return;
    const r = await req('PATCH', `/client-desk/asks/${detail.ask.id}`, { open });
    if (r.success) { onToast(open ? '다시 열었어요' : '마감했어요. 팀장님 화면에서 「올려 주세요」가 사라져요.', 'success'); await loadDetail(detail.ask.id); await load(); }
    else onToast(r.error || '바꾸지 못했어요', 'error');
  }

  if (err) return <div style={{ ...card, padding: 16, color: C.red }}>{err} <button type="button" style={btn()} onClick={load}>다시 불러오기</button></div>;
  if (!asks) return <div style={{ ...card, padding: 16, color: C.sub }}>불러오는 중…</div>;

  // ── 요청 하나 ──
  if (openId) {
    const cols = narrow ? '1fr' : 'minmax(0,1.3fr) 110px minmax(0,1.6fr) 300px';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <button type="button" style={{ ...btn('quiet'), alignSelf: 'flex-start', padding: 0 }} onClick={() => setOpenId(null)}>‹ 자료 요청 목록</button>
        {!detail ? <div style={{ ...card, padding: 16, color: C.sub }}>불러오는 중…</div> : (
          <>
            <div style={{ ...card, padding: '14px 16px', display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
              <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                <div style={{ fontSize: T.lg, fontWeight: 800, ...ell }}>{detail.ask.title}</div>
                <div style={{ fontSize: T.sm, color: C.sub, marginTop: 4 }}>{md(detail.ask.due_date)}까지, {TARGET_KO[detail.ask.target] || ''}, 받을 것: {detail.ask.items.join(', ')}</div>
                {detail.ask.guide && <div style={{ fontSize: T.sm, color: MUTED, marginTop: 2, ...ell }}>안내: {detail.ask.guide}</div>}
              </div>
              {canEdit && (detail.ask.status === 'open'
                ? <button type="button" style={btn()} onClick={() => close(false)}>마감하기</button>
                : <button type="button" style={btn()} onClick={() => close(true)}>다시 열기</button>)}
            </div>
            <div style={card}>
              {!narrow && (
                <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 12, padding: '10px 16px', background: '#F8FAFC', borderBottom: `1px solid ${C.border}`, fontSize: T.sm, fontWeight: 700, color: C.sub }}>
                  <span>업체 · 팀장님</span><span>상태</span><span>올린 파일 · 보고서</span><span>할 일</span>
                </div>
              )}
              {detail.rows.map((l) => (
                <div key={l.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 12, alignItems: 'center', padding: '10px 16px', minHeight: 52, fontSize: T.md }}>
                    <div style={{ minWidth: 0 }}>
                      <button type="button" onClick={() => onOpenCompany(l.company_id)} style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: T.md, fontWeight: 700, color: C.text, maxWidth: '100%', textAlign: 'left', ...ell, display: 'block' }} title={l.company_name}>{l.company_name}</button>
                      <div style={{ fontSize: T.sm, color: C.sub, ...ell }}>{l.fps.length ? `${l.fps.join(', ')} 팀장님` : '이어진 팀장님 없음'}</div>
                    </div>
                    <div><Pill s={l.status} />{l.status === 'more' && l.note ? <div style={{ fontSize: T.sm, color: '#92400E', marginTop: 4, ...ell }} title={l.note}>{l.note}</div> : null}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                      {l.files.length === 0 && !l.report && <span style={{ fontSize: T.sm, color: MUTED }}>아직 없음</span>}
                      {l.files.map((f) => <button key={f.id} type="button" onClick={() => openFile(f.id)} style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', fontSize: T.sm, color: NAVY, textDecoration: 'underline', ...ell }} title={f.name}>{f.name}{f.day ? ` (${md(f.day)})` : ''}</button>)}
                      {l.report && <button type="button" onClick={() => openFile(l.report!.id)} style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', fontSize: T.sm, color: '#166534', fontWeight: 700, textDecoration: 'underline', ...ell }}>보고서: {l.report.name}</button>}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: narrow ? 'flex-start' : 'flex-end' }}>
                      {(l.status === 'uploaded' || l.status === 'more') && <button type="button" style={btn('line', busy === l.id)} disabled={busy === l.id} onClick={() => setRow(l, 'reviewing')}>검토 중으로</button>}
                      {l.status !== 'reported' && l.status !== 'skipped' && <button type="button" style={btn('line', busy === l.id)} disabled={busy === l.id} onClick={() => setMore(more && more.id === l.id ? null : { id: l.id, text: l.note })}>더 올려 주세요</button>}
                      {l.status !== 'skipped' && (
                        <label style={{ ...btn(l.status === 'reported' ? 'line' : 'pri', busy === l.id), display: 'inline-flex', alignItems: 'center' }}>
                          {l.status === 'reported' ? '보고서 다시 보내기' : '보고서 보내기'}
                          <input type="file" accept="application/pdf,.pdf" style={{ display: 'none' }} disabled={busy === l.id} onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) void sendReport(l, f); }} />
                        </label>
                      )}
                      {l.status === 'skipped'
                        ? <button type="button" style={btn('quiet')} onClick={() => setRow(l, 'asked', '', `${l.company_name} · 다시 요청해요`)}>다시 요청</button>
                        : l.status !== 'reported' && <button type="button" style={btn('quiet')} onClick={() => setRow(l, 'skipped', '', `${l.company_name} · 이 요청에서 뺐어요`)}>빼기</button>}
                    </div>
                  </div>
                  {more && more.id === l.id && (
                    <div style={{ display: 'flex', gap: 6, padding: '0 16px 12px' }}>
                      <input autoFocus value={more.text} maxLength={200} onChange={(e) => setMore({ id: l.id, text: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter' && more.text.trim()) void setRow(l, 'more', more.text.trim(), `${l.company_name} · 더 올려 주세요를 보냈어요`); }}
                        placeholder="팀장님께 보일 한 줄, 예) 9월 시산표가 흐려요. 다시 찍어 주세요" style={{ ...input, flex: 1 }} />
                      <button type="button" style={btn('pri', !more.text.trim())} disabled={!more.text.trim()} onClick={() => setRow(l, 'more', more.text.trim(), `${l.company_name} · 더 올려 주세요를 보냈어요`)}>보내기</button>
                    </div>
                  )}
                </div>
              ))}
              {detail.rows.length === 0 && <div style={{ padding: 16, color: C.sub }}>대상 업체가 없어요</div>}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ ...card, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 260px', fontSize: T.sm, color: C.sub, lineHeight: 1.6 }}>
          팀장님 고객관리 화면에 「자료 올려 주세요」를 띄우고, 올라온 자료로 검토 보고서를 보내는 곳이에요. 팀장님 달력 · 거래처 화면 · 아침 문자에 같이 보여요.
        </div>
        {canEdit && !making && <button type="button" style={btn('pri')} onClick={() => setMaking(true)}>새 자료 요청</button>}
      </div>
      {making && <NewAsk onCancel={() => setMaking(false)} onDone={async (id) => { setMaking(false); await load(); setOpenId(id); }} onToast={onToast} narrow={narrow} />}
      <div style={card}>
        {asks.length === 0 && <div style={{ padding: '20px 16px', color: C.sub, fontSize: T.md }}>아직 자료 요청이 없어요.{canEdit ? ' 「새 자료 요청」으로 가결산 자료부터 받아 보세요.' : ''}</div>}
        {asks.map((a) => (
          <button key={a.id} type="button" onClick={() => setOpenId(a.id)}
            style={{ width: '100%', display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'minmax(0,1fr) auto', gap: 10, alignItems: 'center', padding: '12px 16px', border: 'none', borderBottom: `1px solid ${C.border}`, background: '#fff', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: T.md, fontWeight: 800, color: C.text, ...ell }}>{a.title}{a.status === 'closed' ? <span style={{ color: MUTED, fontWeight: 600 }}> (마감)</span> : null}</span>
              <span style={{ display: 'block', fontSize: T.sm, color: C.sub, ...ell }}>{md(a.due_date)}까지, {TARGET_KO[a.target] || ''}, 받을 것 {a.items.length}가지</span>
            </span>
            <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: narrow ? 'flex-start' : 'flex-end' }}>
              {(['asked', 'more', 'uploaded', 'reviewing', 'reported'] as const).filter((k) => a.counts[k]).map((k) => <Pill key={k} s={k} n={a.counts[k]} />)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function NewAsk({ onCancel, onDone, onToast, narrow }: { onCancel: () => void; onDone: (id: string) => void; onToast: (m: string, k?: 'success' | 'error') => void; narrow: boolean }) {
  const [title, setTitle] = useState('');
  const [items, setItems] = useState('');
  const [guide, setGuide] = useState('');
  const [due, setDue] = useState('');
  const [target, setTarget] = useState<'corp' | 'all' | 'pick'>('corp');
  const [report, setReport] = useState('');
  const [list, setList] = useState<Target[]>([]);
  const [corpN, setCorpN] = useState<number | null>(null);
  const [pick, setPick] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    req<Target[]>('GET', '/client-desk/ask-targets?target=all').then((r) => { if (r.success) setList(r.data); }).catch(() => undefined);
    req<Target[]>('GET', '/client-desk/ask-targets?target=corp').then((r) => { if (r.success) setCorpN(r.data.length); }).catch(() => undefined);
  }, []);
  const itemList = items.split('\n').map((x) => x.trim()).filter(Boolean);
  const n = target === 'corp' ? corpN ?? 0 : target === 'all' ? list.length : pick.length;
  const ready = !!title.trim() && itemList.length > 0 && !!due && n > 0;
  async function make() {
    if (!ready || busy) return;
    setBusy(true);
    try {
      const r = await req<{ id: string; n: number }>('POST', '/client-desk/asks', { title: title.trim(), items: itemList, guide: guide.trim(), due_date: due, target, company_ids: pick, report_title: report.trim() });
      if (r.success) { onToast(`${r.data.n}곳 팀장님 화면에 「${title.trim()}」를 띄웠어요`, 'success'); onDone(r.data.id); }
      else onToast(r.error || '만들지 못했어요', 'error');
    } catch (e) { onToast(e instanceof Error ? e.message : '만들지 못했어요', 'error'); }
    finally { setBusy(false); }
  }
  const lab: CSSProperties = { fontSize: T.sm, fontWeight: 700, color: C.sub, marginBottom: 4, display: 'block' };
  return (
    <div style={{ ...card, padding: 16, display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: 14 }}>
      <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8 }}>
        <b style={{ fontSize: T.lg, flex: 1 }}>새 자료 요청</b>
        <button type="button" style={btn()} onClick={() => { setTitle(PRECLOSE.title); setItems(PRECLOSE.items); setGuide(PRECLOSE.guide); }}>가결산 예시로 채우기</button>
      </div>
      <label><span style={lab}>이름</span><input style={input} value={title} maxLength={40} onChange={(e) => setTitle(e.target.value)} placeholder="예) 가결산 자료" /></label>
      <label><span style={lab}>기한(팀장님 달력에 이 날 표시)</span><input style={input} type="date" value={due} onChange={(e) => setDue(e.target.value)} /></label>
      <label style={{ gridColumn: narrow ? 'auto' : '1 / 2' }}><span style={lab}>받을 것(한 줄에 하나)</span>
        <textarea value={items} onChange={(e) => setItems(e.target.value)} rows={4} placeholder={'시산표(1~9월)\n직전 연도 재무제표'} style={{ ...input, height: 'auto', padding: 10, resize: 'vertical', lineHeight: 1.5 }} />
      </label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <label><span style={lab}>팀장님께 보일 안내 한 줄</span><input style={input} value={guide} maxLength={300} onChange={(e) => setGuide(e.target.value)} placeholder="예) 연말 결산 전에 절세 방안을 미리 검토해 드려요" /></label>
        <label><span style={lab}>보고서 이름(비우면 「{(title.trim() || '○○').replace(/\s*자료$/, '')} 검토 보고서」)</span><input style={input} value={report} maxLength={40} onChange={(e) => setReport(e.target.value)} /></label>
      </div>
      <div style={{ gridColumn: '1 / -1' }}>
        <span style={lab}>대상(고객관리와 이어진 팀장님의 거래처)</span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {([['corp', `법인 거래처 ${corpN ?? ''}곳`], ['all', `모든 거래처 ${list.length}곳`], ['pick', `골라서${pick.length ? ` ${pick.length}곳` : ''}`]] as ['corp' | 'all' | 'pick', string][]).map(([k, l]) => (
            <button key={k} type="button" aria-pressed={target === k} onClick={() => setTarget(k)} style={{ ...btn(target === k ? 'pri' : 'line'), borderRadius: 16 }}>{l}</button>
          ))}
        </div>
        {target === 'pick' && (
          <div style={{ marginTop: 8, maxHeight: 220, overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: 8, padding: 8, display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'repeat(3, minmax(0,1fr))', gap: 4 }}>
            {list.map((c) => (
              <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: T.md, minWidth: 0 }}>
                <input type="checkbox" checked={pick.includes(c.id)} onChange={(e) => setPick(e.target.checked ? [...pick, c.id] : pick.filter((x) => x !== c.id))} />
                <span style={ell} title={c.name}>{c.name}</span>
              </label>
            ))}
            {list.length === 0 && <span style={{ color: C.sub, fontSize: T.sm }}>고객관리와 이어진 거래처가 없어요</span>}
          </div>
        )}
      </div>
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
        <span style={{ fontSize: T.sm, color: C.sub, marginRight: 'auto' }}>{ready ? `${n}곳 팀장님 화면에 띄워요` : '이름, 받을 것, 기한, 대상을 채워 주세요'}</span>
        <button type="button" style={btn()} onClick={onCancel}>닫기</button>
        <button type="button" style={btn('pri', !ready || busy)} disabled={!ready || busy} onClick={make}>{busy ? '만드는 중…' : '띄우기'}</button>
      </div>
    </div>
  );
}
